/** @jsxImportSource @emotion/react */
import React from "react";
import { css } from "@emotion/react";
import { withMyTheme } from "../../theme/theme";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import CollectionsIcon from '@mui/icons-material/Collections';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { mobileCss } from "../../util/isMobile";

export const HOW_TO_ID = "how-to";

const SectionContainerStyle = withMyTheme((theme) => css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5rem 2rem;
  background-color: ${theme.palette.background.default};
  
  ${mobileCss(`
    padding: 3rem 1rem;
  `)}
`);

const TitleStyle = withMyTheme((theme) => css`
  font-family: 'Great Vibes', serif;
  font-size: 3rem;
  color: ${theme.palette.primary.main};
  text-align: center;
  font-family: ${theme.typography.h1.fontFamily};
  margin-bottom: 3rem;
  
  ${mobileCss(`
    font-size: 2.5rem;
    margin-bottom: 2rem;
  `)}
`);

const StepsContainerStyle = withMyTheme(() => css`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 2rem;
  width: 100%;
  max-width: 80vw;
  
  ${mobileCss(`
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
    padding-left: ${numberSize/2}px;
  `)}
`);

const StepWrapperStyle = css`
  position: relative;
  margin: 1rem;
  display: flex;
  flex: 1;
  
  /* Make all wrappers the same height */
  @media (min-width: 768px) {
    height: 100%;
  }
`;

const StepColumnStyle = withMyTheme((theme) => css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 20vw;
  padding: 2rem;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%; /* Take full height of parent */
  
  /* Make content fill the available space */
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  }
  
  ${mobileCss(`
    width: 80%;
    margin-bottom: 1rem;
    padding: 1.5rem;
  `)}
`);

const IconContainerStyle = withMyTheme((theme) => css`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 80px;
  height: 80px;
  margin-bottom: 1.5rem;
  background-color: ${theme.palette.primary.light};
  border-radius: 50%;
  color: ${theme.palette.primary.contrastText};
  
  svg {
    font-size: 2.5rem;
  }
  
  ${mobileCss(`
    width: 70px;
    height: 70px;
    
    svg {
      font-size: 2rem;
    }
  `)}
`);

const StepTitleStyle = withMyTheme((theme) => css`
  font-family: 'Cormorant', serif;
  font-size: 1.8rem;
  font-weight: 600;
  color: ${theme.palette.primary.main};
  text-align: center;
  margin-bottom: 1rem;
  
  ${mobileCss(`
    font-size: 1.6rem;
  `)}
`);

const StepDescriptionStyle = withMyTheme((theme) => css`
  font-family: 'Cormorant', serif;
  font-size: 1.2rem;
  color: ${theme.palette.text.primary};
  text-align: center;
  line-height: 1.6;
  flex-grow: 1; /* Make description grow to fill available space */
  
  ${mobileCss(`
    font-size: 1.1rem;
  `)}
`);

const numberSize = 40;

const StepNumberStyle = withMyTheme((theme) => css`
  position: absolute;
  top: -15px;
  left: -15px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${numberSize}px;
  height: ${numberSize}px;
  background-color: ${theme.palette.secondary.main};
  border-radius: 50%;
  color: ${theme.palette.secondary.contrastText};
  font-family: 'Great Vibes', serif;
  font-size: 1.5rem;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`);

const ConnectorStyle = withMyTheme((theme) => css`
  position: absolute;
  top: 40px;
  right: -35%;
  width: 70%;
  height: 2px;
  background: linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent);
  
  ${mobileCss(`
    display: none;
  `)}
`);

export const HowTo = () => {
  const { t } = useTranslation();
  
  return (
    <div css={SectionContainerStyle} id={HOW_TO_ID}>
      <Typography variant="h2" css={TitleStyle}>
        {t('howTo.title')}
      </Typography>
      
      <div css={StepsContainerStyle}>
        <div css={StepWrapperStyle}>
          <div css={StepColumnStyle}>
            <div css={StepNumberStyle}>1</div>
            <div css={IconContainerStyle}>
              <CollectionsIcon />
            </div>
            <Typography css={StepTitleStyle}>
              {t('howTo.steps.step1.title')}
            </Typography>
            <Typography css={StepDescriptionStyle}>
              {t('howTo.steps.step1.description')}
            </Typography>
          </div>
          <div css={ConnectorStyle}></div>
        </div>
        
        <div css={StepWrapperStyle}>
          <div css={StepColumnStyle}>
            <div css={StepNumberStyle}>2</div>
            <div css={IconContainerStyle}>
              <AutoAwesomeIcon />
            </div>
            <Typography css={StepTitleStyle}>
              {t('howTo.steps.step2.title')}
            </Typography>
            <Typography css={StepDescriptionStyle}>
              {t('howTo.steps.step2.description')}
            </Typography>
          </div>
          <div css={ConnectorStyle}></div>
        </div>
        
        <div css={StepWrapperStyle}>
          <div css={StepColumnStyle}>
            <div css={StepNumberStyle}>3</div>
            <div css={IconContainerStyle}>
              <FavoriteIcon />
            </div>
            <Typography css={StepTitleStyle}>
              {t('howTo.steps.step3.title')}
            </Typography>
            <Typography css={StepDescriptionStyle}>
              {t('howTo.steps.step3.description')}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};
